import { FC, ReactNode, useRef, useState, useCallback } from "react";
import 'swiper/css';
import 'swiper/css/pagination';
import { Swiper, SwiperSlide } from 'swiper/react';
import type { Swiper as SwiperType } from 'swiper';
import { Pagination } from 'swiper/modules';
import { useMediaQuery } from '@mui/material';
import SwiperNavButton from "../../SwiperNavButton";
import LazyImage from "../../LazyImage";

// Define an interface for image objects with optional small version
interface ImageWithSmall {
    large: string;
    small?: string;
}

interface ImageCarouselProps {
    editButton?: ReactNode;
    images: ImageWithSmall[];
}

const ImageCarousel: FC<ImageCarouselProps> = ({ editButton, images }) => {
    const swiperRef = useRef<SwiperType>();
    const [isStart, setIsStart] = useState(true);
    const [isEnd, setIsEnd] = useState(false);
    const [activeIndex, setActiveIndex] = useState(0);
    const isMobile = useMediaQuery('(max-width:640px)');

    const updateNavState = useCallback(() => {
        if (swiperRef.current) {
            setIsStart(swiperRef.current.isBeginning);
            setIsEnd(swiperRef.current.isEnd);
            setActiveIndex(swiperRef.current.realIndex);
        }
    }, []);

    const handlePrev = (e: React.MouseEvent) => {
        e.preventDefault();
        if (swiperRef.current && !isStart) {
            swiperRef.current.slidePrev();
            setTimeout(updateNavState, 100);
        }
    };

    const handleNext = (e: React.MouseEvent) => {
        e.preventDefault();
        if (swiperRef.current && !isEnd) {
            swiperRef.current.slideNext();
            setTimeout(updateNavState, 100);
        }
    };

    const renderCustomPagination = () => {
        if (!isMobile) return null;

        const totalDots = Math.min(images.length, 5);
        const dots = [];

        for (let i = 0; i < totalDots; i++) {
            let isActive = false;
            if (images.length <= 5) {
                isActive = i === activeIndex;
            } else {
                if (activeIndex < 3) {
                    isActive = i === activeIndex;
                } else if (activeIndex >= images.length - 3) {
                    isActive = i === totalDots - (images.length - activeIndex);
                } else {
                    isActive = i === 2;
                }
            }

            dots.push(
                <span
                    key={i}
                    className={`inline-block h-2 w-2 rounded-full mx-1 ${isActive ? 'bg-white' : 'bg-white/50'
                        }`}
                />
            );
        }

        return (
            <div className="absolute bottom-4 left-0 right-0 flex justify-center z-30">
                {dots}
            </div>
        );
    };

    // Determine the height class based on mobile state
    const heightClass = isMobile 
        ? 'h-[300px] min-h-[300px]' 
        : 'h-[400px] min-h-[400px]';

    return (
        <div 
            className={`relative sm:rounded-xl bg-gray-200 ${heightClass}`}
        >
            {!isMobile && (
                <SwiperNavButton
                    direction="prev"
                    onClick={handlePrev}
                    disabled={isStart}
                    className={isStart ? 'opacity-0' : ''}
                />
            )}

            <Swiper
                onSwiper={(swiper) => {
                    swiperRef.current = swiper;
                }}
                slidesPerView={1}
                spaceBetween={isMobile ? 0 : 15}
                pagination={{
                    clickable: true,
                    renderBullet: () => '',
                }}
                modules={[Pagination]}
                className="mySwiper sm:rounded-xl"
                breakpoints={{
                    640: {
                        slidesPerView: 'auto',
                        spaceBetween: 15,
                        freeMode: true,
                    },
                }}
                onSlideChange={updateNavState}
                onAfterInit={updateNavState}
            >
                {images.length > 0 ? (
                    images.map((image, index) => (
                        <SwiperSlide key={index} className={isMobile ? '' : 'custom-swiper-slide'}>
                            <div className="relative w-full h-full">
                                <LazyImage
                                    src={image.large}
                                    alt={`Slide ${index + 1}`}
                                    className={`w-full object-cover ${isMobile ? 'h-[300px] min-h-[300px]' : 'h-[400px] custom-slide-image'} select-none`}
                                    smallImageSrc={image.small}
                                />
                            </div>
                        </SwiperSlide>
                    ))
                ) : (
                    <SwiperSlide>
                        <div className={`w-full ${heightClass} bg-gray-200`}>
                            {/* Empty state */}
                            <div className="flex items-center justify-center h-full">
                                <svg
                                    className="w-12 h-12 text-gray-300"
                                    xmlns="http://www.w3.org/2000/svg"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="1.5"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                >
                                    <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
                                    <circle cx="12" cy="13" r="4" />
                                </svg>
                            </div>
                        </div>
                    </SwiperSlide>
                )}
            </Swiper>

            {renderCustomPagination()}

            {editButton}
            
            {images.length > 0 && (
                <div className="bg-black px-2 py-1 rounded-xl shadow-md absolute top-2 right-2 flex justify-center items-center z-10 text-white bg-opacity-50 text-base">
                    <div>
                        <svg
                            className="flex-shrink-0 size-6 md:size-7"
                            xmlns="http://www.w3.org/2000/svg"
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        >
                            <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
                            <circle cx="12" cy="13" r="4" />
                        </svg>
                    </div>
                    <div className="pl-2">
                        {images.length}
                    </div>
                </div>
            )}

            {!isMobile && (
                <SwiperNavButton
                    direction="next"
                    onClick={handleNext}
                    disabled={isEnd}
                    className={isEnd ? 'opacity-0' : ''}
                />
            )}
        </div>
    );
};

export default ImageCarousel;